import type { DirectoryUser } from '@wf-coe/types';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';

/**
 * Search Entra users via the `/v1/directory/users` proxy. Disabled
 * until the query string has 2+ characters so the picker doesn't fan
 * out massive Graph queries on every keystroke. Caller debounces
 * upstream of this hook.
 *
 * Surfaces three failure modes via standard TanStack Query state:
 *   - `isError` with a 503 — env not configured (Entra app reg + secret
 *     missing). The Admins modal falls back to manual entry.
 *   - `isError` with a 502 — upstream Graph failed. Same fallback.
 *   - `isError` with a 403 — caller doesn't have `directory:read`
 *     globally (Viewer). The picker hides itself.
 */
export function useDirectoryUsersQuery(query: string | undefined): UseQueryResult<DirectoryUser[]> {
  const api = useApiClient();
  const trimmed = (query ?? '').trim();
  return useQuery({
    queryKey: ['directory-users', trimmed],
    queryFn: () => {
      if (!api) throw new Error('useDirectoryUsersQuery: api client unavailable');
      return api.get<DirectoryUser[]>(`/v1/directory/users?q=${encodeURIComponent(trimmed)}`);
    },
    enabled: api !== undefined && trimmed.length >= 2,
    // Cache aggressively — Graph results are stable for a given query.
    staleTime: 60_000,
    retry: false,
  });
}
